/**
 * CDN 图片展示 URL 统一入口（主机改写 + WebP）。
 *
 * 对齐旧站 vueMixin.handleImgToWebp，并将测试/正式图床地址归一到当前环境：
 * - 线上：使用 cdnimg-v2，再追加 webp 参数
 * - 本地：使用 cdnimg-test（见 lib/config/cdn.ts），再追加 webp
 *
 * 用法约定：
 * - 数据层 / API 只保留原始 CDN URL，不要预写 webp 参数
 * - JSX 中的 <img>/<Image> 由 Vite 插件 tools/vite-plugin-img-to-webp.ts
 *   在编译期自动包上本方法（对齐旧站 vue-loader replaceLoader）
 * - 字符串模板 HTML / background-image 等仍需手动调用或走 rewriteHtmlImgToWebp
 * - 保证第一次进入 DOM 的地址就是最终展示 URL，避免二次请求
 */

import { rewriteCatalogCdnUrl } from './cdn';

const WEBP_PROCESS =
  'x-image-process=image/format,webp/ignore-error,1&eo-img.format=webp';

/** Spine / 音视频等不应追加图片处理参数的扩展名 */
const NON_IMAGE_EXT =
  /\.(?:skel|atlas|json|mp4|webm|m3u8|mp3|ogg|wav|aac|svg|js|css|txt|html|xml)(?:$|\?)/i;

/** 图鉴 payload 中的 Spine 资源字段，禁止转 WebP */
const SPINE_MEDIA_KEYS = new Set([
  'json',
  'atlas',
  'skel',
  'texture',
]);

export const IMG_SIZE_QUERY = {
  mini: '?eo-img.resize=w/50',
  small: '?eo-img.resize=w/100',
  icon: '?eo-img.resize=w/200',
  list: '?eo-img.resize=w/300',
  middle: '?eo-img.resize=w/800',
  hd: '?eo-img.resize=w/1600',
} as const;

export type ImgSizeKey = keyof typeof IMG_SIZE_QUERY;

function isNetworkUrl(url: string) {
  return /^(https?:\/\/|\/\/)/i.test(url);
}

/** 是否应跳过 WebP（Spine / 非图片资源）；主机改写仍会执行。 */
export function shouldSkipImgToWebp(
  url: string,
  fieldKey?: string | null,
): boolean {
  if (!url) return true;
  if (fieldKey && SPINE_MEDIA_KEYS.has(fieldKey.toLowerCase())) return true;
  const pathOnly = url.split(/[?#]/)[0] || url;
  if (NON_IMAGE_EXT.test(pathOnly)) return true;
  // Spine 纹理目录下的 png 由运行时按 atlas 加载，不能加图片处理参数
  if (/\/spine\//i.test(pathOnly) && /\.(?:png|jpe?g|webp)$/i.test(pathOnly)) {
    return true;
  }
  return false;
}

/**
 * 图片展示 URL：环境主机改写 + webp 查询参数。
 * 非网络地址原样返回；已含 format=webp 时不重复追加。
 * Spine / 非图片仍做主机改写，但不追加 webp。
 */
export function handleImgToWebp(
  url: string | null | undefined,
  fieldKey?: string | null,
): string {
  if (!url) return '';
  if (!isNetworkUrl(url)) return url;

  const rewritten = rewriteCatalogCdnUrl(url);
  if (shouldSkipImgToWebp(rewritten, fieldKey)) return rewritten;
  if (rewritten.includes('format=webp')) return rewritten;
  return rewritten.includes('?')
    ? `${rewritten}&${WEBP_PROCESS}`
    : `${rewritten}?${WEBP_PROCESS}`;
}

/**
 * 处理图片尺寸（对齐旧站 handleImgSize），默认 list。
 * 可与 handleImgToWebp 组合：handleImgToWebp(handleImgSize(url, 'list'))
 */
export function handleImgSize(
  url: string | null | undefined,
  size: ImgSizeKey = 'list',
  defaultUrl = '',
): string {
  if (!url) return defaultUrl;
  if (!isNetworkUrl(url)) return url;
  if (url.includes('thirdqq') || url.includes('data:image')) return url;

  let next = url;
  if (next.includes('x-image-process') || next.includes('x-oss-process')) {
    next = next.split('?')[0] || next;
  }
  return next + IMG_SIZE_QUERY[size];
}

/**
 * 展示用图片 URL（与 handleImgToWebp 等价，保留别名方便语义区分）。
 */
export function toDisplayImageUrl(
  url: string | null | undefined,
  fieldKey?: string | null,
): string {
  return handleImgToWebp(url, fieldKey);
}

/** @deprecated 使用 handleImgToWebp；保留兼容旧调用名。 */
export function toCatalogDisplayImageUrl(url: string): string {
  return handleImgToWebp(url);
}

export function toCatalogDisplayImageUrls(
  urls: string[] | null | undefined,
): string[] {
  if (!Array.isArray(urls)) return [];
  return urls.map(toCatalogDisplayImageUrl);
}

/**
 * 将 HTML 中 <img src> / srcset 的网络图片转为展示 URL（主机改写 + WebP）。
 */
export function rewriteHtmlImgToWebp(html: string): string {
  if (!html || typeof html !== 'string') return html;

  let out = html.replace(
    /(<img\b[^>]*?\bsrc\s*=\s*)(["'])(.*?)\2/gi,
    (full, prefix: string, quote: string, src: string) => {
      const next = handleImgToWebp(src);
      if (next === src) return full;
      return `${prefix}${quote}${next}${quote}`;
    },
  );

  out = out.replace(
    /(<img\b[^>]*?\bsrcset\s*=\s*)(["'])(.*?)\2/gi,
    (full, prefix: string, quote: string, srcset: string) => {
      const next = srcset
        .split(',')
        .map((part) => {
          const trimmed = part.trim();
          if (!trimmed) return part;
          const bits = trimmed.split(/\s+/);
          const src = bits[0] || '';
          const descriptor = bits.slice(1).join(' ');
          const converted = handleImgToWebp(src);
          return descriptor ? `${converted} ${descriptor}` : converted;
        })
        .join(', ');
      if (next === srcset) return full;
      return `${prefix}${quote}${next}${quote}`;
    },
  );

  out = out.replace(
    /(style\s*=\s*)(["'])(.*?)\2/gi,
    (full, prefix: string, quote: string, style: string) => {
      const next = style.replace(
        /url\(\s*(['"]?)(https?:\/\/[^)'"]+|\/\/[^)'"]+)\1\s*\)/gi,
        (match, innerQuote: string, src: string) => {
          const converted = handleImgToWebp(src);
          if (converted === src) return match;
          return `url(${innerQuote}${converted}${innerQuote})`;
        },
      );
      if (next === style) return full;
      return `${prefix}${quote}${next}${quote}`;
    },
  );

  return out;
}
